import { format } from 'date-fns'
import { TFunction } from 'i18next'
import { DataItem } from 'scrivito'

import { TimeRecordRow } from '../types'
import { ensureString } from '@/utils/ensureString'

interface TimeRecordInput {
  taskId: string
  date: string
  hours: number
  clientId: string
  orderId: string
  positionId: string
  activityType: string
  description: string
  isBillable: boolean
  productType: string
  internalNote: string
  groupKey: string
}

const readTimeRecord = (item: DataItem): TimeRecordInput | null => {
  const recordedAt = ensureString(item.get('recordedAt'))
  const date = recordedAt ? format(new Date(recordedAt), 'yyyy-MM-dd') : ''
  if (!date) return null

  const clientId = ensureString(item.get('client'))
  const orderId = ensureString(item.get('order'))
  const positionId = ensureString(item.get('position'))
  const activityType = ensureString(item.get('activityType'))

  return {
    taskId: item.id(),
    date,
    hours: (item.get('totalDuration') as number) || 0,
    clientId,
    orderId,
    positionId,
    activityType,
    description: ensureString(item.get('description')),
    isBillable: item.get('billable') === true,
    productType: ensureString(item.get('productType')),
    internalNote: ensureString(item.get('internalNote')),
    groupKey: `${clientId}-${orderId}-${positionId}-${activityType}`,
  }
}

const resolveNames = (
  input: TimeRecordInput,
  clientMap: Map<string, string>,
  orderMap: Map<string, string>,
  positionMap: Map<string, string>,
  t: TFunction,
) => ({
  clientName:
    clientMap.get(input.clientId) ||
    input.clientId ||
    t('timeTracking.fallback.unknownClient'),
  orderName:
    orderMap.get(input.orderId) ||
    input.orderId ||
    t('timeTracking.fallback.unknownOrder'),
  positionCode:
    positionMap.get(input.positionId) ||
    input.positionId ||
    t('timeTracking.fallback.notAvailable'),
})

const createRow = (
  input: TimeRecordInput,
  names: { clientName: string; orderName: string; positionCode: string },
): TimeRecordRow => ({
  taskId: input.taskId,
  taskName: names.positionCode,
  projectName: names.clientName,
  projectCode: names.orderName,
  description: input.internalNote,
  dailyEntries: {
    [input.date]: [
      {
        recordId: input.taskId,
        hours: input.hours,
        activityType: input.activityType,
        description: input.description,
      },
    ],
  },
  totalHours: input.hours,
  isBillable: input.isBillable,
  clientId: input.clientId,
  orderId: input.orderId,
  positionId: input.positionId,
  productType: input.productType,
  activityType: input.activityType,
})

const appendEntry = (existing: TimeRecordRow, input: TimeRecordInput) => {
  const entries = existing.dailyEntries[input.date] ?? []
  entries.push({
    recordId: input.taskId,
    hours: input.hours,
    activityType: input.activityType,
    description: input.description,
  })
  existing.dailyEntries[input.date] = entries
  existing.totalHours += input.hours
}

export const groupTimeRecords = (
  timeRecordItems: DataItem[],
  clientMap: Map<string, string>,
  orderMap: Map<string, string>,
  positionMap: Map<string, string>,
  t: TFunction,
): TimeRecordRow[] => {
  const groupedRecords = new Map<string, TimeRecordRow>()

  timeRecordItems.forEach((item) => {
    const input = readTimeRecord(item)
    if (!input) return

    const existing = groupedRecords.get(input.groupKey)
    if (existing) {
      appendEntry(existing, input)
      return
    }

    const names = resolveNames(input, clientMap, orderMap, positionMap, t)
    groupedRecords.set(input.groupKey, createRow(input, names))
  })

  return Array.from(groupedRecords.values())
}
